import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Settings2, Video, Timer, Check, Loader2 } from 'lucide-react';
import { getYoutubeId } from '../FeaturedVideo';
import { resolveMedia } from '../../lib/media';
import UploadMediaButton from './UploadMediaButton';

const HomeSettingsPanel = ({ settings, setSettings, onSave, saving }) => {
  const update = (field, value) => setSettings({ ...settings, [field]: value });

  return (
    <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }} data-testid="home-settings-panel">
      <h3 className="text-white font-bold flex items-center gap-2 mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
        <Settings2 size={18} className="text-orange-500" /> IMPOSTAZIONI HOME
      </h3>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 p-3 rounded border border-gray-800 bg-gray-900/50">
          <div>
            <p className="text-white text-sm font-bold">Tabellone finalisti in home</p>
            <p className="text-gray-500 text-xs">Mostra o nascondi la griglia dei finalisti sopra i tabelloni</p>
          </div>
          <Switch
            data-testid="finalists-enabled-switch"
            className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-gray-700"
            checked={!!settings.finalists_enabled}
            onCheckedChange={v => update('finalists_enabled', v)}
          />
        </div>

        <div>
          <label className="text-gray-400 text-xs uppercase tracking-wider">Titolo tabellone</label>
          <Input
            data-testid="finalists-title-input"
            value={settings.finalists_title || ''}
            onChange={e => update('finalists_title', e.target.value)}
            placeholder="FINALISTI DELLA EDIZIONE 2026"
            className="bg-gray-900 border-gray-700 text-white font-bold mt-1"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between gap-3 p-3 rounded border border-gray-800 bg-gray-900/50">
            <div>
              <p className="text-white text-sm font-bold">Bottone ISCRIZIONI 2026</p>
              <p className="text-gray-500 text-xs">Apre il form di iscrizione al torneo</p>
            </div>
            <Switch
              data-testid="registration-btn-switch"
              className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-gray-700"
              checked={!!settings.show_registration_btn}
              onCheckedChange={v => update('show_registration_btn', v)}
            />
          </div>
          <div className="flex items-center justify-between gap-3 p-3 rounded border border-gray-800 bg-gray-900/50">
            <div>
              <p className="text-white text-sm font-bold">Bottone PREMI DEL VINCITORE</p>
              <p className="text-gray-500 text-xs">Mostra il popup con i premi nazionali</p>
            </div>
            <Switch
              data-testid="prizes-btn-switch"
              className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-gray-700"
              checked={!!settings.show_prizes_btn}
              onCheckedChange={v => update('show_prizes_btn', v)}
            />
          </div>
        </div>

        <div className="pt-2 border-t border-gray-800">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-white text-sm font-bold flex items-center gap-2">
              <Timer size={15} className="text-red-500" /> Countdown finale nazionale
            </p>
            <Switch
              data-testid="countdown-enabled-switch"
              className="data-[state=checked]:bg-orange-600 data-[state=unchecked]:bg-gray-700"
              checked={!!settings.countdown_enabled}
              onCheckedChange={v => update('countdown_enabled', v)}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input
              type="date"
              data-testid="countdown-date-input"
              value={settings.countdown_date || ''}
              onChange={e => update('countdown_date', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white"
            />
            <Input
              data-testid="countdown-label-input"
              value={settings.countdown_label || ''}
              onChange={e => update('countdown_label', e.target.value)}
              placeholder="FINALE NAZIONALE"
              className="bg-gray-900 border-gray-700 text-white"
            />
          </div>
        </div>

        <div className="pt-2 border-t border-gray-800">
          <p className="text-white text-sm font-bold flex items-center gap-2 mb-1">
            <Video size={15} className="text-red-500" /> Video in evidenza (sopra SPOT e SHORTS)
          </p>
          <p className="text-gray-500 text-xs mb-2">
            Incolla un link YouTube, l'URL di un file mp4, oppure carica un video. Lascia vuoto per nascondere il video.
          </p>
          <div className="flex gap-1.5">
            <Input
              data-testid="featured-video-url-input"
              value={settings.featured_video_url || ''}
              onChange={e => update('featured_video_url', e.target.value)}
              placeholder="https://youtu.be/... oppure https://.../video.mp4 — oppure CARICA (max 50 MB)"
              className="bg-gray-900 border-gray-700 text-white font-mono text-xs flex-1"
            />
            <UploadMediaButton idx="featured-video" accept="video/mp4,video/webm,video/quicktime" onUploaded={(url) => update('featured_video_url', url)} />
          </div>
          <Input
            data-testid="featured-video-title-input"
            value={settings.featured_video_title || ''}
            onChange={e => update('featured_video_title', e.target.value)}
            placeholder="Titolo sopra il video (opzionale)"
            className="bg-gray-900 border-gray-700 text-white mt-2"
          />
          {settings.featured_video_url?.trim() && (
            <div className="mt-3" data-testid="featured-video-preview">
              <p className="text-gray-400 text-xs mb-1.5 uppercase tracking-wider">Anteprima live</p>
              <div className="relative w-full max-w-md bg-black rounded-lg overflow-hidden border border-gray-700" style={{ aspectRatio: '16 / 9' }}>
                {getYoutubeId(settings.featured_video_url) ? (
                  <iframe
                    key={getYoutubeId(settings.featured_video_url)}
                    src={`https://www.youtube.com/embed/${getYoutubeId(settings.featured_video_url)}?rel=0&modestbranding=1`}
                    title="Anteprima video"
                    data-testid="featured-video-preview-youtube"
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                    allow="encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                ) : (
                  <video
                    key={settings.featured_video_url}
                    src={resolveMedia(settings.featured_video_url)}
                    data-testid="featured-video-preview-mp4"
                    className="absolute inset-0 w-full h-full object-contain"
                    controls
                    muted
                    playsInline
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <Button
          data-testid="home-settings-save-btn"
          onClick={onSave}
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
        >
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
          SALVA IMPOSTAZIONI
        </Button>
      </div>
    </div>
  );
};

export default HomeSettingsPanel;
